import PropTypes from "prop-types";
import React, { Component } from "react";
import withStyles from "@material-ui/core/styles/withStyles";
import { get, keys, map } from "lodash";
import { numberToBRL } from "services/utils";

import InfoGrid from "components/EnergyCompany/Grid/InfoGrid.jsx";
import InfoGridItem from "components/EnergyCompany/Grid/InfoGridItem.jsx";
import Table from "components/Table/Table.jsx";
import Tooltip from "@material-ui/core/Tooltip";
import lightTooltipStyle from "assets/EnergyCompany/jss/lightTooltip";

const simuladorStyle = theme => ({
  spacedInfo: {
    justifyContent: "space-between",
    display: "flex",
    width: "90%",
  },
  centeredInfo: {
    justifyContent: "center",
    display: "flex",
    width: "90%",
  },
  dataWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  tooltipLabel: {
    marginRight: '10px',
  },
  tableWrapper: {
    "& table": {
      width: "1500px",
      "& tr td": {
        borderRight: "1px solid #dddddd",
        fontWeight: "500",

        "&:first-child": {
          fontWeight: "bold",
          width: "220px",
        },
        "&:last-child": {
          borderRight: "none",
        },
      },
    },
  },
  ...lightTooltipStyle(theme),
});

class ResultadoSimuladorCativoLivre extends Component {
  getTableData() {
    const { classes, resultadoEconomia } = this.props;
    const { comparacoesAnuais, descricoesCustos } = resultadoEconomia;
    const anos = keys(comparacoesAnuais);

    const getTooltip = (value, details) => (
      <Tooltip
        classes={{ popper: classes.lightTooltipPopper, tooltip: classes.lightTooltip }}
        placement="top"
        title={details.length ? (
          map(details, (detail, index) => {
            const parts = detail.split(':');
            return (
              <div key={index}>
                <b className={classes.tooltipLabel}>
                  {parts[0]}:
                </b>
                <span>{parts[1]}</span>
              </div>
            );
          })
        ) : 'Sem detalhes no momento.'}
      >
        <span>{value}</span>
      </Tooltip>
    );

    // Linhas da descrição
    const data = map(descricoesCustos, ({ descricao }) => [
      <b key="0">{descricao}</b>,
      ...map(anos, ano => {
        let custoCativo = get(comparacoesAnuais[ano], `custosCativo[${descricao}].custo`, 0);
        let custoCativoDetalhado = get(comparacoesAnuais[ano], `custosCativo[${descricao}].custoDetalhado`, []);
        let custoLivre = get(comparacoesAnuais[ano], `custosLivre[${descricao}].custo`, 0);
        let custoLivreDetalhado = get(comparacoesAnuais[ano], `custosLivre[${descricao}].custoDetalhado`, []);

        return (
          <div className={classes.dataWrapper} key={ano}>
            <div className={classes.spacedInfo}>
              {getTooltip(numberToBRL(custoCativo), custoCativoDetalhado)}
              {getTooltip(numberToBRL(custoLivre), custoLivreDetalhado)}
            </div>
          </div>
        );
      }),
    ]);

    // Total
    data.push([
      <b key="0">Total</b>,
      ...map(anos, ano => {
        let custoCativo = get(comparacoesAnuais[ano], `economia.custoCativo`, 0);
        let custoLivre = get(comparacoesAnuais[ano], `economia.custoLivre`, 0);
        return (
          <div className={classes.dataWrapper} key={ano}>
            <div className={classes.spacedInfo}>
              <span>{numberToBRL(custoCativo)}</span><span>{numberToBRL(custoLivre)}</span>
            </div>
          </div>
        );
      }),
    ]);

    // Economia
    data.push([
      <b key="0">Economia</b>,
      ...map(anos, ano => {
        let economia = get(comparacoesAnuais[ano], "economia.economia", 0);
        let economiaPercentual = get(comparacoesAnuais[ano], "economia.economiaPercentual", 0);
        return (
          <div className={classes.dataWrapper} key={ano}>
            <div className={classes.centeredInfo}>
              <span>{numberToBRL(economia)} ({economiaPercentual.toFixed(2)}%)</span>
            </div>
          </div>
        );
      }),
    ]);

    // BreakEven
    data.push([
      <b key="0">BreakEven</b>,
      ...map(anos, ano => {
        let breakEven = get(comparacoesAnuais[ano], "precoBreakEven", 0);
        return (
          <div className={classes.dataWrapper} key={ano}>
            <div className={classes.centeredInfo}>
              <span>{numberToBRL(breakEven)}</span>
            </div>
          </div>
        );
      }),
    ]);

    return data;
  }

  getTableHead() {
    const { classes, resultadoEconomia } = this.props;
    const anos = keys(resultadoEconomia.comparacoesAnuais);

    return [
      <b key="0">Dados</b>,
      ...map(anos, ano => (
        <div className={classes.dataWrapper} key={ano}>
          <b>{ano}</b>
          <div className={classes.spacedInfo}>
            <b>Cativo</b><b>Livre</b>
          </div>
        </div>
      )),
    ];
  }

  render() {
    const { classes, resultadoEconomia } = this.props;

    return (
      <div>
        <h3>
          <b>Resultado</b>
        </h3>
        <h6>
          Passe o mouse sobre os valores para ver mais detalhes.
        </h6>
        <div className={classes.tableWrapper}>
          <Table
            tableData={this.getTableData()}
            tableHead={this.getTableHead()}
            tableHeaderColor="primary"
          />
          <InfoGrid>
            <InfoGridItem
              label="Total Cativo"
              modifiers={["forceNewFullRowModifier"]}
              value={numberToBRL(get(resultadoEconomia, "comparacaoTotal.economia.custoCativo", 0))}
            />
            <InfoGridItem
              label="Total Livre"
              modifiers={["forceNewFullRowModifier"]}
              value={numberToBRL(get(resultadoEconomia, "comparacaoTotal.economia.custoLivre", 0))}
            />
            <InfoGridItem
              label="Economia"
              modifiers={["forceNewFullRowModifier"]}
              value={numberToBRL(get(resultadoEconomia, "comparacaoTotal.economia.economia", 0)) + " (" + get(resultadoEconomia, "comparacaoTotal.economia.economiaPercentual", 0).toFixed(2) + "%)"}
            />
            <InfoGridItem
              label="Break Even"
              modifiers={["forceNewFullRowModifier"]}
              value={numberToBRL(get(resultadoEconomia, "comparacaoTotal.precoBreakEven", 0))}
            />
          </InfoGrid>
        </div>
      </div>
    );
  }
}

ResultadoSimuladorCativoLivre.propTypes = {
  classes: PropTypes.object.isRequired,
  resultadoEconomia: PropTypes.shape({
    comparacaoTotal: PropTypes.object.isRequired,
    comparacoesAnuais: PropTypes.object.isRequired,
    descricoesCustos: PropTypes.array.isRequired,
  }).isRequired,
};

export { ResultadoSimuladorCativoLivre as ResultadoSimuladorCativoLivreComponent };
export default withStyles(simuladorStyle)(ResultadoSimuladorCativoLivre);



// WEBPACK FOOTER //
// ./src/views/Pages/partials/ResultadoSimuladorCativoLivre.jsx